/**
 * JSON-LD Schema Utilities
 * Provides structured data schemas for SEO optimization
 */

const SITE_ROOT = 'https://africaresearchuniversity.com';

/**
 * Organization schema - describes the organization
 */
export const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': 'Africa Research University',
    'url': SITE_ROOT,
    'logo': `${SITE_ROOT}/assets/img/arulogo.png`,
    'description': 'Africa Research University - Excellence in research and higher education across Africa',
    'sameAs': [
        'https://www.facebook.com/africaresearchuniversity',
        'https://twitter.com/africaresearchuniversity',
        'https://www.linkedin.com/company/africa-research-university',
        'https://www.instagram.com/africaresearchuniversity/'
    ],
    'contactPoint': {
        '@type': 'ContactPoint',
        'telephoneNumber': '+1234567890',
        'contactType': 'Customer Support'
    },
    'address': {
        '@type': 'PostalAddress',
        'addressCountry': 'ZA'
    }
};

/**
 * Website schema - for homepage
 */
export const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'url': SITE_ROOT,
    'name': 'Africa Research University',
    'description': 'Excellence in research and higher education across Africa',
    'potentialAction': {
        '@type': 'SearchAction',
        'target': {
            '@type': 'EntryPoint',
            'urlTemplate': `${SITE_ROOT}?s={search_term_string}`
        },
        'query-input': 'required name=search_term_string'
    }
};

/**
 * BreadcrumbList schema - for navigation paths
 */
export const createBreadcrumbSchema = (breadcrumbs) => {
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': breadcrumbs.map((item, index) => ({
            '@type': 'ListItem',
            'position': index + 1,
            'name': item.name,
            'item': item.url
        }))
    };
};

/**
 * Course schema - for individual courses
 */
export const createCourseSchema = (course) => {
    return {
        '@context': 'https://schema.org',
        '@type': 'Course',
        'name': course.title,
        'description': course.introduction || course.description || '',
        'provider': {
            '@type': 'Organization',
            'name': 'Africa Research University',
            'url': SITE_ROOT
        },
        'url': course.url || SITE_ROOT,
        'inLanguage': 'en',
        ...(course.image && { 'image': course.image }),
        ...(course.duration && { 'duration': course.duration }),
        ...(course.level && { 'educationLevel': course.level })
    };
};

/**
 * Article/NewsArticle schema - for blog posts and articles
 */
export const createArticleSchema = (article) => {
    return {
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        'headline': article.title,
        'description': article.excerpt || article.description || '',
        'image': article.image ? [article.image] : [],
        'datePublished': article.datePublished || article.date || new Date().toISOString(),
        ...(article.dateModified && { 'dateModified': article.dateModified }),
        'author': {
            '@type': 'Organization',
            'name': article.author || 'Africa Research University'
        },
        'publisher': {
            '@type': 'Organization',
            'name': 'Africa Research University',
            'logo': {
                '@type': 'ImageObject',
                'url': `${SITE_ROOT}/assets/img/arulogo.png`
            }
        },
        'url': article.url || SITE_ROOT
    };
};

/**
 * EducationalOrganization schema - for university details
 */
export const educationalOrganizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    'name': 'Africa Research University',
    'url': SITE_ROOT,
    'logo': `${SITE_ROOT}/assets/img/arulogo.png`,
    'description': 'Excellence in research and higher education across Africa',
    'foundingDate': '2020',
    'areaServed': 'Africa',
    'educationalCredentialAwarded': ['Bachelor Degree', 'Master Degree', 'PhD'],
};

/**
 * FAQPage schema - for frequently asked questions
 */
export const createFAQSchema = (faqs) => {
    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        'mainEntity': faqs.map(faq => ({
            '@type': 'Question',
            'name': faq.question,
            'acceptedAnswer': {
                '@type': 'Answer',
                'text': faq.answer
            }
        }))
    };
};

/**
 * LocalBusiness schema - for contact information
 */
export const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    'name': 'Africa Research University',
    'image': `${SITE_ROOT}/assets/img/arulogo.png`,
    'description': 'Excellence in research and higher education across Africa',
    'url': SITE_ROOT,
    'telephone': '+1234567890',
    'address': {
        '@type': 'PostalAddress',
        'addressCountry': 'ZA',
        'addressRegion': 'South Africa',
    },
};
